"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { AgencyRestrictions, AgencyStatusResponse } from "@/types/agencies";
import { apiLive } from "@/lib/api";
import { GetRestrictions } from "@/utils/common/restrictions";

type restrictedText = {
  type:
    | "campaign"
    | "sequences"
    | "redialingRules"
    | "contacts"
    | "contactLists"
    | "filesUpload";
  key: "maxTotal" | "maxUpload" | "maxConcurrency" | "maxContacts";
};

type RestrictionsProps = {
  restrictions: AgencyRestrictions;
  isDefault: boolean;
  getRestrictionValue: (restrictedText: restrictedText) => number;
  getRestrictedText: (
    restrictedText: restrictedText,
    currentValue: number
  ) => string;
};

export const RestrictionsContext = createContext<RestrictionsProps>({
  restrictions: GetRestrictions(null),
  isDefault: true,
  getRestrictionValue: () => 0,
  getRestrictedText: () => "",
});

export const useRestrictions = () => useContext(RestrictionsContext)!;

const useRestrictionsFunction = (): RestrictionsProps => {
  const { user } = useAuthContext();
  const { t } = useTranslation("restrictions");
  const isLoadingRef = useRef<boolean>(false);

  const [data, setData] = useState<{
    restrictions: AgencyRestrictions;
    isDefault: boolean;
  }>({
    restrictions: GetRestrictions(null),
    isDefault: true,
  });

  const fetchRestrictions = async () => {
    isLoadingRef.current = true;

    const response = await apiLive<AgencyStatusResponse>(`/api/settings/agency/status`
    );

    if (response && response.restrictions) {
      setData({
        restrictions: response.restrictions,
        isDefault: false,
      });
    }

    isLoadingRef.current = false;
  };

  useEffect(() => {
    if (user) {
      if (isLoadingRef.current || !data.isDefault) {
        return;
      }

      fetchRestrictions();
    } else {
      setData({
        restrictions: GetRestrictions(null),
        isDefault: true,
      });
    }
  }, [user]);

  const getRestrictionValue = (restrictedText: restrictedText): number => {
    const restrictions = data.restrictions;

    switch (restrictedText.type) {
      case "campaign":
        switch (restrictedText.key) {
          case "maxTotal":
            return restrictions.campaign?.maxTotal ?? 0;
          case "maxConcurrency":
            return restrictions.campaign?.maxConcurrency ?? 0;
          case "maxContacts":
            return restrictions.campaign?.maxContacts ?? 0;
          default:
            return 0;
        }
      case "sequences":
        switch (restrictedText.key) {
          case "maxTotal":
            return restrictions.sequences?.maxTotal ?? 0;
          default:
            return 0;
        }
      case "redialingRules":
        switch (restrictedText.key) {
          case "maxTotal":
            return restrictions.redialingRules?.maxTotal ?? 0;
          default:
            return 0;
        }
      case "contacts":
        switch (restrictedText.key) {
          case "maxTotal":
            return restrictions.contacts?.maxTotal ?? 0;
          case "maxUpload":
            return restrictions.contacts?.maxUpload ?? 0;
          default:
            return 0;
        }
      case "contactLists":
        switch (restrictedText.key) {
          case "maxTotal":
            return restrictions.contactLists?.maxTotal ?? 0;
          default:
            return 0;
        }
      case "filesUpload":
        switch (restrictedText.key) {
          case "maxTotal":
            return restrictions.filesUpload?.maxTotal ?? 0;
          default:
            return 0;
        }
    }
  };

  const getRestrictedText = (
    restrictedText: restrictedText,
    currentValue: number
  ): string => {
    const value = getRestrictionValue(restrictedText);

    if (value <= -1 || value > currentValue) {
      return "";
    }

    return t(`${restrictedText.type}.${restrictedText.key}`, {
      count: value,
    });
  };

  return {
    restrictions: data.restrictions,
    isDefault: data.isDefault,
    getRestrictionValue,
    getRestrictedText,
  };
};

export const RestrictionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const profileFunction = useRestrictionsFunction();

  return (
    <RestrictionsContext.Provider value={profileFunction}>
      {children}
    </RestrictionsContext.Provider>
  );
};
